import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Search,
  Filter,
  MessageSquare,
  Video,
  Calendar,
  Star,
  Edit,
  Plus,
  Send,
  Loader2,
  ChevronRight,
  Users,
  CheckCircle2,
  Upload
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../ui/utils";
import { BulkCommunicationPanel } from "./BulkCommunicationPanel";
import API from "../api/api01";
import { useEffect } from "react";

interface Contact {
  id: string;
  name: string;
  email: string;
  phone: string;
  alternatePhone?: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  course: string;
  priority: "High" | "Medium" | "Low";
  source: "Website" | "Walk-in" | "Campaign" | "Referral";
  counselor: {
    name: string;
    avatar: string;
  };
  lastContact: string;
  rating: number;
}

export function LeadContact() {
  const [searchQuery, setSearchQuery] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [isBulkCommunicationOpen, setIsBulkCommunicationOpen] = useState(false);
  const [isCommTypeDialogOpen, setIsCommTypeDialogOpen] = useState(false);
  const [isCommChoiceDialogOpen, setIsCommChoiceDialogOpen] = useState(false);
  const [pendingCommType, setPendingCommType] = useState<"call" | "mail" | "message" | "whatsapp">("message");
  const [tempSingleContact, setTempSingleContact] = useState<Contact | null>(null);
  const [selectedCommType, setSelectedCommType] = useState<string>("email");
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState<Contact[]>([]);

  // Form states (Restored)
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editAlternatePhone, setEditAlternatePhone] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editPincode, setEditPincode] = useState("");
  const [editCourse, setEditCourse] = useState("");
  const [editPriority, setEditPriority] = useState<"High" | "Medium" | "Low">("Medium");

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await API.get("/lms-contacts/");
      const data = response.data || [];

      const mappedContacts: Contact[] = data.map((c: any) => ({
        id: c.id,
        name: c.name,
        email: c.email,
        phone: c.phone || "",
        alternatePhone: "",
        address: c.address || "N/A",
        city: c.city || "N/A",
        state: "N/A",
        pincode: "N/A",
        course: c.course || "N/A",
        priority: (c.priority as any) || "Medium",
        source: (c.source as any) || "Website",
        counselor: {
          name: c.counselor_name || "Unassigned",
          avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${c.id}`
        },
        lastContact: c.last_contact || "Never",
        rating: 0
      }));

      if (mappedContacts.length > 0) {
        toast.success("Contacts loaded");
      } else {
        toast.error("No records found");
      }
      setContacts(mappedContacts);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
      toast.error("Failed to load contacts from system.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "High":
        return "bg-red-500/10 text-red-500 border-red-500/20";
      case "Medium":
        return "bg-orange-500/10 text-orange-500 border-orange-500/20";
      case "Low":
        return "bg-green-500/10 text-green-500 border-green-500/20";
      default:
        return "";
    }
  };

  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch =
      contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.phone.includes(searchQuery) ||
      contact.city.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter =
      priorityFilter === "all" || contact.priority === priorityFilter;

    return matchesSearch && matchesFilter;
  });

  const handleEditContact = (contact: Contact) => {
    setSelectedContact(contact);
    setEditName(contact.name);
    setEditEmail(contact.email);
    setEditPhone(contact.phone);
    setEditAlternatePhone(contact.alternatePhone || "");
    setEditAddress(contact.address);
    setEditCity(contact.city);
    setEditState(contact.state);
    setEditPincode(contact.pincode);
    setEditCourse(contact.course);
    setEditPriority(contact.priority);
    setIsEditDialogOpen(true);
  };

  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(false);
  const [isMailDialogOpen, setIsMailDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Contact | null>(null);
  const [messageText, setMessageText] = useState("");
  const [mailSubject, setMailSubject] = useState("");
  const [mailText, setMailText] = useState("");


  const handlePriorityChange = async (contactId: string, newPriority: Contact["priority"]) => {
    try {
      // Optimistic update
      setContacts(prev => prev.map(c =>
        c.id === contactId ? { ...c, priority: newPriority } : c
      ));

      const contact = contacts.find(c => c.id === contactId);
      if (!contact) return;

      const updatedData = {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        alternate_phone: contact.alternatePhone || "",
        address: contact.address,
        city: contact.city,
        state: contact.state,
        pincode: contact.pincode,
        course: contact.course,
        priority: newPriority,
      };

      await API.put(`/lms-contacts/${contactId}`, updatedData);
      toast.success(`Priority updated to ${newPriority}`);
    } catch (error) {
      console.error("Failed to update priority:", error);
      toast.error("Failed to update priority");
      fetchContacts(); // Revert to server state
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedContact) return;

    try {
      const updatedData = {
        name: editName,
        email: editEmail,
        phone: editPhone,
        alternate_phone: editAlternatePhone,
        address: editAddress,
        city: editCity,
        state: editState,
        pincode: editPincode,
        course: editCourse,
        priority: editPriority,
      };

      await API.put(`/lms-contacts/${selectedContact.id}`, updatedData);

      toast.success("Contact updated successfully!");
      setIsEditDialogOpen(false);
      fetchContacts();
    } catch (error: any) {
      console.error("Failed to update contact:", error);
      const detail = error.response?.data?.detail;
      const errorMessage = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: any) => `${d.loc.join('.')}: ${d.msg}`).join(', ')
          : "Failed to update contact.";
      toast.error(errorMessage);
    }
  };

  const handleSelectContact = (contactId: string, checked: boolean) => {
    if (checked) {
      setSelectedContactIds((prev) => [...prev, contactId]);
    } else {
      setSelectedContactIds((prev) => prev.filter((id) => id !== contactId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedContactIds(filteredContacts.map((c) => c.id));
    } else {
      setSelectedContactIds([]);
    }
  };

  const finalSelectedContacts = tempSingleContact 
    ? [tempSingleContact] 
    : contacts.filter((c) => selectedContactIds.includes(c.id));

  const handleOpenBulkCommunication = () => {
    if (selectedContactIds.length === 1) {
      const singleContact = contacts.find(c => c.id === selectedContactIds[0]);
      if (singleContact) {
        setTempSingleContact(singleContact);
      }
    } else {
      setTempSingleContact(null);
    }
    setIsCommTypeDialogOpen(true);
  };

  const handleCommAction = (contact: Contact, type: "call" | "mail" | "message" | "whatsapp") => {
    setTempSingleContact(contact);
    setPendingCommType(type);
    setIsCommChoiceDialogOpen(true);
  };

  const proceedWithSingle = () => {
    setIsCommChoiceDialogOpen(false);
    if (!tempSingleContact) return;
    
    setSelectedLead(tempSingleContact);
    if (pendingCommType === "call") setIsCallDialogOpen(true);
    if (pendingCommType === "mail") {
      setMailSubject("");
      setMailText("");
      setIsMailDialogOpen(true);
    }
    if (pendingCommType === "message") {
      setMessageText("");
      setIsMessageDialogOpen(true);
    }
    // We don't have a single whatsapp dialog yet, so we could default it to bulk or add one later
  };

  const handleSelectCommType = (type: string) => {
    setSelectedCommType(type);
    setIsCommTypeDialogOpen(false);
    
    // If only 1 lead is selected (via checkbox), proceed in "old way" (single dialog)
    if (selectedContactIds.length === 1) {
      const singleContact = contacts.find(c => c.id === selectedContactIds[0]);
      if (singleContact) {
        setSelectedLead(singleContact);
        if (type === "email") {
          setMailSubject("");
          setMailText("");
          setIsMailDialogOpen(true);
        } else if (type === "message") {
          setMessageText("");
          setIsMessageDialogOpen(true);
        } else if (type === "whatsapp") {
          // Fallback to bulk panel for whatsapp as there's no single dialog yet, 
          // or we can add a toast if preferred. 
          setIsBulkCommunicationOpen(true);
        }
        return;
      }
    }

    // Multiple or Zero selected: Open Bulk Panel
    setIsBulkCommunicationOpen(true);
  };

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 bg-background min-h-full">
      {/* Header */}
      <div>
        <h1>Lead Contacts</h1>
        <p className="text-muted-foreground mt-1">
          Manage contact information and communication channels
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Contacts", val: contacts.length, icon: Phone, color: "text-blue-500", bg: "bg-blue-500/10", glow: "from-blue-500/0 via-blue-500 to-blue-500/0", hoverBg: "from-blue-500/10" },
          { label: "High Priority", val: contacts.filter(c => c.priority === "High").length, icon: Star, color: "text-red-500", bg: "bg-red-500/10", glow: "from-red-500/0 via-red-500 to-red-500/0", hoverBg: "from-red-500/10" },
          { label: "Email Verified", val: contacts.length, icon: Mail, color: "text-purple-500", bg: "bg-purple-500/10", glow: "from-purple-500/0 via-purple-500 to-purple-500/0", hoverBg: "from-purple-500/10" },
          { label: "Cities", val: new Set(contacts.map(c => c.city)).size, icon: MapPin, color: "text-orange-500", bg: "bg-orange-500/10", glow: "from-orange-500/0 via-orange-500 to-orange-500/0", hoverBg: "from-orange-500/10" }
        ].map(s => (
          <Card key={s.label} className="glass-card group relative overflow-hidden shadow-sm border-none bg-slate-50 dark:bg-slate-900/40">
            {/* Premium Deep Glass Highlight */}
            <div className={cn("absolute top-0 left-0 w-full h-[3px] transition-all duration-500 opacity-30 group-hover:opacity-100 bg-gradient-to-r", s.glow)} />
            <div className={cn("absolute inset-0 transition-all duration-700 opacity-0 group-hover:opacity-100 bg-gradient-to-br via-transparent to-transparent", s.hoverBg)} />

            {/* Darker Tint Overlay on Hover */}
            <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 dark:bg-white/5" />

            <CardContent className="p-6 relative z-10">
              <div className="flex items-center gap-3">
                <div className={cn("p-3 rounded-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg", s.bg)}>
                  <s.icon className={cn("h-6 w-6", s.color)} />
                </div>
                <div>
                  <p className="text-2xl font-bold group-hover:translate-x-1 transition-transform duration-300">{s.val}</p>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters & Action Bar */}
      <Card className="shadow-sm bg-card border-border overflow-hidden">
        <CardContent className="p-4">
          <div className="flex flex-col xl:flex-row gap-4 items-center justify-between">

            {/* Left side: Search & Dropdown */}
            <div className={cn(
              "flex flex-col md:flex-row gap-4 w-full transition-all duration-500 ease-in-out",
              selectedContactIds.length > 0 ? "xl:w-1/3" : "xl:w-full"
            )}>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl h-12 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800"
                />
              </div>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full md:w-[180px] rounded-xl h-12 bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 shrink-0">
                  <SelectValue placeholder="All Priorities" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border shadow-2xl rounded-xl z-50">
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="High">High Priority</SelectItem>
                  <SelectItem value="Medium">Medium Priority</SelectItem>
                  <SelectItem value="Low">Low Priority</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Right side: Action Bar */}
            <div className="flex-shrink-0 w-full xl:w-auto overflow-x-auto pb-2 xl:pb-0 hide-scrollbar">
              <AnimatePresence mode="popLayout">
                <motion.div
                  initial={{ opacity: 0, x: 20, scale: 0.95 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 20, scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  className={cn(
                    "border shadow-md rounded-2xl p-2 px-3 flex items-center gap-4 md:gap-6 min-w-max transition-all duration-300",
                    selectedContactIds.length > 0 
                      ? "bg-white dark:bg-[#0f172a] border-slate-200 dark:border-slate-800" 
                      : "bg-slate-50/50 dark:bg-slate-900/30 border-slate-100 dark:border-slate-800/50"
                  )}
                >
                  <div className="flex items-center gap-3 md:gap-4 border-r border-slate-200 dark:border-slate-800 pr-4 md:pr-6">
                    <div className={cn(
                      "h-10 w-10 md:h-12 md:w-12 rounded-xl flex items-center justify-center shadow-inner shrink-0 transition-colors",
                      selectedContactIds.length > 0 ? "bg-primary/10 text-primary" : "bg-slate-200 dark:bg-slate-800 text-slate-400"
                    )}>
                      <Users className="h-5 w-5 md:h-6 md:w-6" />
                    </div>
                    <div className="flex flex-col hidden sm:flex">
                      <div className="flex items-center gap-1 md:gap-2">
                        <span className={cn(
                          "text-base md:text-lg font-black tabular-nums",
                          selectedContactIds.length > 0 ? "text-slate-900 dark:text-white" : "text-slate-400"
                        )}>{selectedContactIds.length}</span>
                        <span className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider">Selected</span>
                      </div>
                      <p className="text-[8px] md:text-[10px] text-primary font-black uppercase tracking-[0.2em]">LMS Hub</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 md:gap-4">
                    {selectedContactIds.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedContactIds([])}
                        className="text-slate-500 hover:text-red-500 font-bold text-[10px] md:text-xs rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 px-2 md:px-3 h-8 md:h-10"
                      >
                        Clear
                      </Button>
                    )}
                    <Button
                      onClick={handleOpenBulkCommunication}
                      className={cn(
                        "rounded-xl px-4 md:px-8 h-10 md:h-12 font-black shadow-md flex items-center gap-2 md:gap-3 transition-all active:scale-95 group overflow-hidden relative border border-white/20",
                        selectedContactIds.length > 0 
                          ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white" 
                          : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500"
                      )}
                    >
                      {selectedContactIds.length > 0 && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite]" />
                      )}
                      <Send className="h-4 w-4 md:h-5 md:w-5 group-hover:-rotate-12 transition-transform duration-500" />
                      <span className="tracking-[0.1em] md:tracking-[0.15em] uppercase text-[9px] md:text-[10px] font-black">
                        {selectedContactIds.length === 0 ? "Manual Bulk Entry" : 
                         selectedContactIds.length === 1 ? "Proceed to Communication" : "Dispatch Bulk Campaign"}
                      </span>
                      <div className="h-4 md:h-5 w-[1px] bg-slate-300 dark:bg-slate-700 mx-0.5 md:mx-1" />
                      <ChevronRight className="h-4 w-4 md:h-5 md:w-5 group-hover:translate-x-1 transition-transform duration-500" />
                    </Button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>


          </div>
        </CardContent>
      </Card>

      {/* Selection Header */}
      {filteredContacts.length > 0 && (
        <div className="flex items-center gap-3 px-2">
          <Checkbox
            checked={
              selectedContactIds.length === filteredContacts.length &&
              filteredContacts.length > 0
            }
            onCheckedChange={handleSelectAll}
            id="select-all"
            className="
    border-2
    data-[state=checked]:bg-primary
    data-[state=checked]:border-primary
    data-[state=checked]:text-white
  "
          />
          <Label htmlFor="select-all" className="text-sm cursor-pointer">
            Select All ({filteredContacts.length} contacts)
          </Label>
        </div>
      )}

      {/* Contact Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredContacts.length === 0 ? (
          <div className="col-span-full py-20 bg-card rounded-2xl border-2 border-dashed border-border flex flex-col items-center justify-center text-slate-400">
            <Users className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-lg font-medium">No records found</p>
            <p className="text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          filteredContacts.map((contact) => (
            <Card key={contact.id} className="shadow-sm hover:shadow-md transition-shadow relative bg-card border-border">
              {/* Selection Checkbox */}
              <div className="absolute top-3 left-3 z-10">
                <Checkbox
                  checked={selectedContactIds.includes(contact.id)}
                  onCheckedChange={(checked) => {
                    handleSelectContact(contact.id, checked as boolean);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="
                    border-2
                    bg-card
                    data-[state=checked]:bg-blue-600
                    data-[state=checked]:border-blue-600
                    data-[state=checked]:text-white
                  "
                />
              </div>
              <CardHeader className="pb-3 pl-12">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={contact.counselor.avatar} />
                      <AvatarFallback>
                        {contact.name.substring(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-base">{contact.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{contact.course}</p>
                    </div>
                  </div>
                  <Select
                    value={contact.priority}
                    onValueChange={(value) => handlePriorityChange(contact.id, value as Contact["priority"])}
                  >
                    <SelectTrigger className={cn(
                      "w-[100px] h-7 text-[10px] font-bold uppercase tracking-wider border-none shadow-none focus:ring-0",
                      getPriorityColor(contact.priority)
                    )}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border shadow-2xl rounded-xl">
                      <SelectItem value="High" className="text-red-500 font-bold">HIGH</SelectItem>
                      <SelectItem value="Medium" className="text-orange-500 font-bold">MEDIUM</SelectItem>
                      <SelectItem value="Low" className="text-green-500 font-bold">LOW</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Contact Info */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{contact.phone}</span>
                  </div>
                  {contact.alternatePhone && (
                    <div className="flex items-center gap-2 text-sm ml-6">
                      <span className="text-muted-foreground">{contact.alternatePhone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground truncate">{contact.email}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span className="text-muted-foreground">
                      {contact.city}, {contact.state} - {contact.pincode}
                    </span>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-4 w-4 ${i < contact.rating
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-300"
                        }`}
                    />
                  ))}
                </div>

                {/* Last Contact */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  Last contact: {contact.lastContact}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-3 gap-2 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full hover:bg-blue-500/10 transition-all"
                    onClick={() => {
                      setSelectedLead(contact);
                      setIsCallDialogOpen(true);
                    }}
                  >
                    <Phone className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full hover:bg-purple-500/10 transition-all" onClick={() => {
                    setSelectedLead(contact);
                    setMailSubject("");
                    setMailText("");
                    setIsMailDialogOpen(true);
                  }}>
                    <Mail className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full hover:bg-emerald-500/10 transition-all" onClick={() => {
                    setSelectedLead(contact);
                    setMessageText("");
                    setIsMessageDialogOpen(true);
                  }}>
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full hover:bg-slate-500/10 transition-all" onClick={() => handleEditContact(contact)}>
                    <Edit className="h-4 w-4" />
                  </Button>


                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Contact Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 sm:max-w-[425px] border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>
              Make changes to the contact information below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="alternatePhone">Alternate Phone</Label>
              <Input
                id="alternatePhone"
                value={editAlternatePhone}
                onChange={(e) => setEditAlternatePhone(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={editAddress}
                onChange={(e) => setEditAddress(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={editCity}
                onChange={(e) => setEditCity(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={editState}
                onChange={(e) => setEditState(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="pincode">Pincode</Label>
              <Input
                id="pincode"
                value={editPincode}
                onChange={(e) => setEditPincode(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="course">Course</Label>
              <Input
                id="course"
                value={editCourse}
                onChange={(e) => setEditCourse(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={editPriority}
                onValueChange={(value) => setEditPriority(value as "High" | "Medium" | "Low")}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveEdit}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="sm:max-w-[550px] bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>
              Message {selectedLead?.name}
            </DialogTitle>
            <DialogDescription>
              Send a message to {selectedLead?.name} at {selectedLead?.phone}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                type="email"
                value={selectedLead?.phone || ""}
                readOnly
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Type your message here..."
                rows={3}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsMessageDialogOpen(false)}
            >
              Cancel
            </Button>

            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                if (!messageText.trim()) {
                  toast.error("Message cannot be empty");
                  return;
                }

                toast.success(`Message sent to ${selectedLead?.name}`);
                setIsMessageDialogOpen(false);
                setMessageText("");
              }}
            >
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Dialog */}
      <Dialog open={isCallDialogOpen} onOpenChange={setIsCallDialogOpen}>
        <DialogContent className="sm:max-w-[550px] bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>
              Call {selectedLead?.name}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to initiate a call to {selectedLead?.name} at {selectedLead?.phone}?
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 rounded-full bg-blue-50 dark:bg-blue-900/20">
                <Phone className="h-12 w-12 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{selectedLead?.name}</p>
                <p className="text-muted-foreground">{selectedLead?.phone}</p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCallDialogOpen(false)}
            >
              Cancel
            </Button>

            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                toast.success(`Calling ${selectedLead?.name}`);
                setIsCallDialogOpen(false);
              }}
            >
              Call Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mail Dialog */}
      <Dialog open={isMailDialogOpen} onOpenChange={setIsMailDialogOpen}>
        <DialogContent className="sm:max-w-[550px] bg-white dark:bg-card text-black dark:text-white rounded-lg shadow-lg p-6 border-slate-200 dark:border-slate-800">
          <DialogHeader>
            <DialogTitle>
              Email {selectedLead?.name}
            </DialogTitle>
            <DialogDescription>
              Send an email to {selectedLead?.name} at {selectedLead?.email}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                type="email"
                value={selectedLead?.email || ""}
                readOnly
              />
            </div>

            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                placeholder="Email Subject"
                value={mailSubject}
                onChange={(e) => setMailSubject(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                placeholder="Type your email body here..."
                rows={4}
                value={mailText}
                onChange={(e) => setMailText(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsMailDialogOpen(false)}
            >
              Cancel
            </Button>

            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                if (!mailSubject.trim() || !mailText.trim()) {
                  toast.error("Subject and Message cannot be empty");
                  return;
                }

                toast.success(`Email sent to ${selectedLead?.name}`);
                setIsMailDialogOpen(false);
                setMailSubject("");
                setMailText("");
              }}
            >
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Communication Type Selection Dialog */}
      <Dialog open={isCommTypeDialogOpen} onOpenChange={setIsCommTypeDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-card border-border rounded-2xl shadow-2xl p-0 overflow-hidden">
          <div className="p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Choose Communication Channel</DialogTitle>
              <DialogDescription className="text-slate-500 dark:text-slate-400">
                Select how you want to reach out to {selectedContactIds.length} contact(s)
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-6 grid grid-cols-1 gap-4">
            <button
              onClick={() => setSelectedCommType("whatsapp")}
              className={cn(
                "group flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 text-left",
                selectedCommType === "whatsapp"
                  ? "border-green-500 bg-green-500/5 shadow-md shadow-green-500/10"
                  : "border-slate-100 dark:border-slate-800 hover:border-slate-200"
              )}
            >
              <div className={cn(
                "h-12 w-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110",
                selectedCommType === "whatsapp" ? "bg-green-500 text-white" : "bg-green-500/10 text-green-600"
              )}>
                <MessageSquare className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold text-slate-900 dark:text-white">WhatsApp Business</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Direct message to recipient's WhatsApp</p>
              </div>
              {selectedCommType === "whatsapp" && <CheckCircle2 className="ml-auto h-5 w-5 text-green-500" />}
            </button>

            <button
              onClick={() => setSelectedCommType("message")}
              className={cn(
                "group flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 text-left",
                selectedCommType === "message"
                  ? "border-blue-500 bg-blue-500/5 shadow-md shadow-blue-500/10"
                  : "border-slate-100 dark:border-slate-800 hover:border-slate-200"
              )}
            >
              <div className={cn(
                "h-12 w-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110",
                selectedCommType === "message" ? "bg-blue-500 text-white" : "bg-blue-500/10 text-blue-600"
              )}>
                <Send className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold text-slate-900 dark:text-white">SMS Message</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Send text message via SMS gateway</p>
              </div>
              {selectedCommType === "message" && <CheckCircle2 className="ml-auto h-5 w-5 text-blue-500" />}
            </button>

            <button
              onClick={() => setSelectedCommType("email")}
              className={cn(
                "group flex items-center gap-4 p-4 rounded-xl border-2 transition-all duration-200 text-left",
                selectedCommType === "email"
                  ? "border-indigo-500 bg-indigo-500/5 shadow-md shadow-indigo-500/10"
                  : "border-slate-100 dark:border-slate-800 hover:border-slate-200"
              )}
            >
              <div className={cn(
                "h-12 w-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110",
                selectedCommType === "email" ? "bg-indigo-500 text-white" : "bg-indigo-500/10 text-indigo-600"
              )}>
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <p className="font-bold text-slate-900 dark:text-white">Email Campaign</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Reach inbox with professional email</p>
              </div>
              {selectedCommType === "email" && <CheckCircle2 className="ml-auto h-5 w-5 text-indigo-500" />}
            </button>
          </div>

          <div className="p-6 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-4">
            <Button variant="ghost" onClick={() => setIsCommTypeDialogOpen(false)} className="font-bold text-slate-500 hover:text-red-500">
              Cancel
            </Button>
            <Button
              onClick={() => {
                setIsCommTypeDialogOpen(false);
                setIsBulkCommunicationOpen(true);
              }}
              disabled={!selectedCommType}
              className="bg-primary hover:bg-primary/90 text-white rounded-xl px-8 h-12 font-bold shadow-lg shadow-primary/20 flex items-center gap-2"
            >
              Proceed to Campaign <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Communication Panel */}

      <BulkCommunicationPanel
        isOpen={isBulkCommunicationOpen}
        onClose={() => {
          setIsBulkCommunicationOpen(false);
          setSelectedCommType("email");
          setTempSingleContact(null); // Clear temp single contact
        }}
        selectedContacts={finalSelectedContacts}
        initialType={selectedCommType}
        module="LMS"
      />

    </div>
  );
}
